/*
Species info loader — mirrors Android SpeciesInfo.kt.
Loads /data/species-info.json (an array of entries keyed by speciesIndex `i`)
and exposes a Map<int, SpeciesEntry> for screens that need Paris-wide stats,
Wikipedia summary, and the city's PDF fiche per species.
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/species-info.json'  // canonical location served by the SW

export interface SpeciesStats
{
	count: number
	proportion: number
	medianHm: number | null
	medianCircCm: number | null
	topArrAbs: Array<{ arr: string; count: number }>
	topArrOver: Array<{ arr: string; ratio: number; count: number }>
}

/*
Descriptions terrain d'identification (bloc `ess.iddesc`). Chaque champ
optionnel — les champs vides sont omis à la source par build_dataset.py,
et un groupe entièrement vide est omis en bloc (parité IdentDescriptions
réduit à null côté Kotlin).
*/
export interface SpeciesIdDesc
{
	ecorce?: string
	feuillage?: string
	floraison?: string
	fructification?: string
}

/* Proses de services écosystémiques (bloc `ess.svc`), mêmes conventions. */
export interface SpeciesSvc
{
	climat?: string
	eau?: string
	biodiv?: string
}

/*
Attributs structurés issus des fiches-essences Ville de Paris (bloc `ess`),
présent seulement sur les espèces matchées (~171/938). Miroir de
SpeciesAttributes (Android SpeciesInfo.kt) — clés JSON courtes conservées
telles quelles, modalités texte déjà en FR lisible (« Caduc », « Pleureur »…).
*/
export interface SpeciesEss
{
	port?: string  // silhouette (« Ovoïde », « Pleureur »…)
	feuillage?: string  // « Caduc », « Persistant »…
	taille?: string  // catégorie floue (« Grand »…), repli quand haut/env absents
	indigenat?: string  // « Exotique », « Indigène »…
	origine?: string
	fleurs?: boolean  // volontairement non affiché (redondant avec la frise floraison)
	expo?: string[]  // exposition
	eau?: string[]  // besoins en eau
	sites?: string[]  // sites de plantation
	// Calendriers en bitfield 12 bits : bit 0 = janvier … bit 11 = décembre.
	// Contrat miroir Python (essence_pdf.py) ↔ Kotlin ↔ TS. Lire via
	// isMonthInBitfield() après validation validBitfield().
	flor?: number
	fruct?: number
	atouts?: string[]
	limites?: string[]
	fam?: string  // famille botanique (« Sapindacée »)
	haut?: string  // hauteur adulte, valeur déjà unitée (« 30 m »)
	env?: string  // envergure adulte (« 15 m »)
	croiss?: string  // vitesse de croissance (« Moyenne »)
	long?: string  // longévité (« Grande (> à 200 ans) »)
	iddesc?: SpeciesIdDesc
	paris?: string  // prose éditoriale « L'essence à Paris »
	svc?: SpeciesSvc
}

export interface SpeciesEntry
{
	i: number
	stats: SpeciesStats
	wp?: string
	qid?: string
	summary?: string
	pdf?: string
	ess?: SpeciesEss
}

/*
Valide un bitfield calendrier : 12 bits utiles, zéro = donnée absente.
Parité Android `optIntOrNull("flor")?.takeIf { it in 1..0xFFF }` — toute
valeur hors plage (0, négatif, NaN, > 0xFFF) est traitée comme manquante.
*/
export function validBitfield(inBits: number | undefined): number | null
{
	if (typeof inBits !== 'number' || !Number.isInteger(inBits)) return null
	return inBits >= 1 && inBits <= 0xFFF ? inBits : null
}

/*
Le mois est-il actif dans le bitfield ? inMonth0 est 0-based (janvier = 0,
soit `Date.prototype.getMonth()`) — l'équivalent Android prend 1..12, le
décalage est absorbé ici.
*/
export function isMonthInBitfield(inBits: number, inMonth0: number): boolean
{
	return ((inBits >> inMonth0) & 1) === 1
}

let fEntries: Map<number, SpeciesEntry> | null = null  // memoized index -> entry, null until first load

/*
Ensure species-info is loaded and indexed by `i`. Idempotent. Throws on
fetch failure; consumers should fall back to a graceful empty state.
*/
export async function ensureSpeciesInfo(): Promise<Map<number, SpeciesEntry>>
{
	if (fEntries) return fEntries
	const vArr = await fetchJson<SpeciesEntry[]>(kAssetPath)  // throws with context on bad JSON
	const vMap = new Map<number, SpeciesEntry>()  // built then handed to module singleton
	for (const vEntry of vArr) vMap.set(vEntry.i, vEntry)
	fEntries = vMap
	return fEntries
}

/*
Look up the entry for a given speciesIndex. Returns null if not loaded yet
or unknown — caller treats this as a soft miss (skip the optional fields).
*/
export function speciesInfoFor(inIndex: number): SpeciesEntry | null
{
	if (!fEntries) return null
	return fEntries.get(inIndex) ?? null
}

/*
« · médiane X (tag) » — parité Android ArbreDetailScreen.medianComparison :
repère compact, pas de percentile. Utilisé par la sheet carte et la fiche
remarquable.
*/
export function medianComparison(inValue: number, inMedian: number | null): string
{
	if (inMedian === null || inMedian <= 0) return ''
	const vRatio = inValue / inMedian
	const vTag = vRatio >= 1.5 ? 'bien au-dessus'
		: vRatio >= 1.15 ? 'au-dessus'
		: vRatio <= 0.66 ? 'bien en dessous'
		: vRatio <= 0.85 ? 'en dessous'
		: 'proche'
	return ` · médiane ${inMedian} (${vTag})`
}
